import ExpandMoreIcon from '@material-design-icons/svg/filled/expand_more.svg?react';
import WarningIcon from '@material-design-icons/svg/filled/warning.svg?react';
import { useState } from 'react';
import {
  formatPermissionName,
  getDangerousPermissions,
  hasDangerousPermissions,
  isDangerousPermission,
} from '../../../../utils/androidUtils';

interface PermissionsSectionProps {
  permissions: string[];
}

export function PermissionsSection({ permissions }: PermissionsSectionProps) {
  const [open, setOpen] = useState(false);

  if (!permissions || permissions.length === 0) {
    return null;
  }

  const dangerousCount = getDangerousPermissions(permissions).length;
  const hasDangerous = hasDangerousPermissions(permissions);
  const sorted = [...permissions].sort((a, b) => {
    const aDangerous = isDangerousPermission(a);
    const bDangerous = isDangerousPermission(b);
    if (aDangerous !== bDangerous) return aDangerous ? -1 : 1;
    return a.localeCompare(b);
  });

  return (
    <section className="rounded-2xl bg-white/[0.02] border border-white/5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center justify-between w-full px-6 py-5 group"
      >
        <div className="flex items-center gap-3">
          <h2 className="text-[13px] font-semibold tracking-wide text-white/90">
            Permissions
          </h2>
          <span className="text-xs text-neutral-500 tabular-nums">
            {permissions.length}
          </span>
          {hasDangerous && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-[10.5px] font-medium text-amber-400">
              <WarningIcon className="size-3 fill-amber-400" />
              {dangerousCount} sensitive
            </span>
          )}
        </div>
        <ExpandMoreIcon
          className={`w-4 h-4 fill-neutral-500 group-hover:fill-neutral-300 transition-all ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div className="px-6 pb-6 border-t border-white/5">
          {hasDangerous && (
            <p className="text-xs text-amber-400/80 mt-4 flex items-center gap-2">
              <WarningIcon className="size-3 fill-amber-400" />
              Sensitive permissions appear first and require careful review.
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-4">
            {sorted.map((permission) => {
              const dangerous = isDangerousPermission(permission);
              return (
                <div
                  key={permission}
                  className="flex items-baseline gap-2.5 min-w-0 py-1"
                >
                  <span
                    className={`w-1 h-1 rounded-full mt-1.5 flex-shrink-0 ${
                      dangerous ? 'bg-amber-400' : 'bg-neutral-600'
                    }`}
                  />
                  <code
                    className={`text-xs break-all font-mono ${
                      dangerous ? 'text-amber-300' : 'text-neutral-400'
                    }`}
                    title={permission}
                  >
                    {formatPermissionName(permission)}
                  </code>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
